import type {
  LoginRequest,
  LoginResponse,
  User,
  CategoriesResponse,
  ArticlesResponse,
  ArticleResponse,
  SearchResponse,
  CommentsResponse,
  ArticleVersionsResponse,
  ArticleVersion,
  APIResponse,
} from "@/types/api";

const API_URL = import.meta.env.VITE_KB_API_URL;

if (!API_URL) {
  throw new Error("VITE_KB_API_URL environment variable is not set");
}

class APIError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "APIError";
  }
}

// Helper function to handle API responses
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    if (response.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem("auth_token");
      window.location.href = "/app/login";
      throw new APIError(401, "Unauthorized");
    }

    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch {
      // Ignore JSON parsing errors, use status message
    }

    throw new APIError(response.status, errorMessage);
  }

  return response.json();
}

// Helper function to make authenticated requests
async function fetchWithAuth(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = localStorage.getItem("auth_token");

  const headers = new Headers(options.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  headers.set("Content-Type", "application/json");

  return fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });
}

// Auth API
export const auth = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    console.log("🔍 API Call: POST /api/auth/login", credentials);
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(credentials),
    });

    const result = await handleResponse<LoginResponse>(response);
    console.log("📦 API Response: POST /api/auth/login", result);
    return result;
  },

  async me(): Promise<User> {
    console.log("🔍 API Call: GET /api/auth/me");
    const response = await fetchWithAuth("/api/auth/me");
    const result = await handleResponse<{ user: User }>(response);
    console.log("📦 API Response: GET /api/auth/me", result);
    return result.user;
  },

  logout() {
    localStorage.removeItem("auth_token");
    window.location.href = "/app/login";
  },
};

// Categories API
export const categories = {
  async list(): Promise<CategoriesResponse> {
    console.log("🔍 API Call: GET /api/categories");
    const response = await fetchWithAuth("/api/categories");
    const result = await handleResponse<CategoriesResponse>(response);
    console.log("📦 API Response: GET /api/categories", result);
    return result;
  },
};

// Articles API
export const articles = {
  async list(params: { category?: string } = {}): Promise<ArticlesResponse> {
    const searchParams = new URLSearchParams();
    if (params.category) {
      searchParams.set("category", params.category);
    }

    const endpoint = `/api/articles${searchParams.toString() ? `?${searchParams}` : ""}`;
    console.log("🔍 API Call: GET", endpoint);

    const response = await fetchWithAuth(endpoint);
    const result = await handleResponse<ArticlesResponse>(response);
    console.log("📦 API Response: GET", endpoint, result);
    return result;
  },

  async getBySlug(slug: string): Promise<ArticleResponse> {
    console.log("🔍 API Call: GET /api/articles/" + slug);
    const response = await fetchWithAuth(`/api/articles/${encodeURIComponent(slug)}`);
    const result = await handleResponse<ArticleResponse>(response);
    console.log("📦 API Response: GET /api/articles/" + slug, result);
    return result;
  },

  async create(data: any): Promise<ArticleResponse> {
    console.log("🔍 API Call: POST /api/articles", data);
    const response = await fetchWithAuth("/api/articles", {
      method: "POST",
      body: JSON.stringify(data),
    });
    const result = await handleResponse<ArticleResponse>(response);
    console.log("📦 API Response: POST /api/articles", result);
    return result;
  },

  async update(slug: string, data: any): Promise<ArticleResponse> {
    console.log("🔍 API Call: PUT /api/articles/" + slug, data);
    const response = await fetchWithAuth(`/api/articles/${encodeURIComponent(slug)}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const result = await handleResponse<ArticleResponse>(response);
    console.log("📦 API Response: PUT /api/articles/" + slug, result);
    return result;
  },

  async delete(slug: string): Promise<{ success: boolean }> {
    console.log("🔍 API Call: DELETE /api/articles/" + slug);
    const response = await fetchWithAuth(`/api/articles/${encodeURIComponent(slug)}`, {
      method: "DELETE",
    });
    const result = await handleResponse<{ success: boolean }>(response);
    console.log("📦 API Response: DELETE /api/articles/" + slug, result);
    return result;
  },
};

// Comments API
export const comments = {
  async list(articleId: number): Promise<CommentsResponse> {
    console.log("🔍 API Call: GET /api/articles/" + articleId + "/comments");
    const response = await fetchWithAuth(`/api/articles/${articleId}/comments`);
    const result = await handleResponse<CommentsResponse>(response);
    console.log("📦 API Response: GET /api/articles/" + articleId + "/comments", result);
    return result;
  },
};

// Search API
export const search = {
  async query(q: string): Promise<SearchResponse> {
    const endpoint = `/api/search?q=${encodeURIComponent(q)}`;
    console.log("🔍 API Call: GET", endpoint);

    const response = await fetchWithAuth(endpoint);
    const result = await handleResponse<SearchResponse>(response);
    console.log("📦 API Response: GET", endpoint, result);
    return result;
  },
};

// Version History API
export const versions = {
  // Get all versions for an article
  async list(articleSlug: string): Promise<ArticleVersionsResponse> {
    const endpoint = `/api/articles/${articleSlug}/versions`;
    console.log("🔍 API Call: GET", endpoint);

    const response = await fetchWithAuth(endpoint);
    const result = await handleResponse<ArticleVersionsResponse>(response);
    console.log("📦 API Response: GET", endpoint, result);
    return result;
  },

  // Get a specific version
  async get(articleSlug: string, versionId: number): Promise<{ version: ArticleVersion }> {
    const endpoint = `/api/articles/${articleSlug}/versions/${versionId}`;
    console.log("🔍 API Call: GET", endpoint);

    const response = await fetchWithAuth(endpoint);
    const result = await handleResponse<{ version: ArticleVersion }>(response);
    console.log("📦 API Response: GET", endpoint, result);
    return result;
  },

  // Revert to a specific version
  async revert(articleSlug: string, versionId: number, changeSummary?: string): Promise<ArticleResponse> {
    const endpoint = `/api/articles/${articleSlug}/revert/${versionId}`;
    console.log("🔍 API Call: POST", endpoint);

    const body = changeSummary ? { change_summary: changeSummary } : {};

    const response = await fetchWithAuth(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    const result = await handleResponse<ArticleResponse>(response);
    console.log("📦 API Response: POST", endpoint, result);
    return result;
  },

  // Compare two versions (future enhancement)
  async compare(articleSlug: string, fromVersion: number, toVersion: number): Promise<{ diff: any }> {
    const endpoint = `/api/articles/${articleSlug}/versions/compare?from=${fromVersion}&to=${toVersion}`;
    console.log("🔍 API Call: GET", endpoint);

    const response = await fetchWithAuth(endpoint);
    const result = await handleResponse<{ diff: any }>(response);
    console.log("📦 API Response: GET", endpoint, result);
    return result;
  },
};

// Users API (for mentions)
export const users = {
  async search(query: string): Promise<{ users: any[] }> {
    console.log("🔍 API Call: GET /api/users?search=" + query);
    const response = await fetchWithAuth(`/api/users?search=${encodeURIComponent(query)}`);
    const result = await handleResponse<{ users: any[] }>(response);
    console.log("📦 API Response: GET /api/users?search=" + query, result);
    return result;
  },
};

// Re-export for convenience
export const kbApi = {
  auth,
  categories,
  articles,
  comments,
  search,
  versions,
  users,
};